import { Button, Flex, Grid, Modal, Space, Spin } from "antd"
import React, { useEffect, useRef, useState } from "react"
import { useDispatch } from "react-redux"
import { useHistory } from "react-router-dom"
import { $t } from "~/i18n.js"
import { colors } from "~/theme/colors.js"
import { config } from "~/config.js"
import { api } from "~/lib/api.js"
import { AddMeldingModal } from "../../melding/sections/addMeldingModal.jsx"
import { addMelding } from "~/redux/materiaal/materiaal.actions.js"
import {
  canvasPoint,
  drawSchematic,
  getImageRect,
  toCanvasPoint,
  toImagePosition,
  useDamageSchematic,
} from "./damageSchematic.js"
import { MeldingStatusTag } from "../../materialTags.jsx"

const MARKER_RADIUS = 16

// Fan out touching markers on a ring around their centre, so no number hides another
const spreadMarkers = (points, radius) => {
  const minDistance = radius * 2 + 2

  // group markers that touch, also via a chain of other markers
  const groups = []
  points.forEach((point, index) => {
    const touching = groups.filter((group) =>
      group.some((i) => Math.hypot(points[i].x - point.x, points[i].y - point.y) < minDistance)
    )
    const merged = touching.flat().concat(index)
    touching.forEach((group) => groups.splice(groups.indexOf(group), 1))
    groups.push(merged)
  })

  const placed = new Array(points.length)
  groups.forEach((group) => {
    if (group.length === 1) {
      const point = points[group[0]]
      placed[group[0]] = { ...point, realX: point.x, realY: point.y }
      return
    }
    const cx = group.reduce((sum, i) => sum + points[i].x, 0) / group.length
    const cy = group.reduce((sum, i) => sum + points[i].y, 0) / group.length
    const ring = minDistance / (2 * Math.sin(Math.PI / group.length))
    group
      .slice()
      .sort((a, b) => a - b)
      .forEach((i, k) => {
        const angle = -Math.PI / 2 + (2 * Math.PI * k) / group.length
        placed[i] = {
          ...points[i],
          x: cx + ring * Math.cos(angle),
          y: cy + ring * Math.sin(angle),
          realX: points[i].x,
          realY: points[i].y,
        }
      })
  })
  return placed
}

export const DamageInspectionModal = ({
  material,
  closeModal,
}) => {
  const screens = Grid.useBreakpoint()
  const wrapperRef = useRef(null)
  const canvasRef = useRef(null)
  const placedRef = useRef([])
  const imageRectRef = useRef(null)
  const photoRefs = useRef({})
  const highlightTimer = useRef(null)
  const { backgroundRef, backgroundLoaded } = useDamageSchematic(material)
  const isMobile = !screens.md

  const dispatch = useDispatch()
  const history = useHistory()
  const [markers, setMarkers] = useState([])
  const [bestanden, setBestanden] = useState([])
  const [bestandenLoaded, setBestandenLoaded] = useState(false)
  const [meldingModalOpen, setMeldingModalOpen] = useState(false)
  const [pendingCoordinates, setPendingCoordinates] = useState(null)
  const [hoverPosition, setHoverPosition] = useState(null)
  const [highlightedMelding, setHighlightedMelding] = useState(null)

  // no clicks before the schematic is shown, else a marker lands on a blank canvas
  const loading = !backgroundLoaded || !bestandenLoaded

  const addMarker = (marker) => {
    setMarkers((prev) => [...prev, marker])
  }

  const fetchBestanden = async () => {
    const result = (await api.get(`materiaal/${material.id}/bestanden.json`)) || []
    setBestandenLoaded(true)
    setBestanden([...result])
    // label is the photo's number in the list below, so marker and photo match
    setMarkers(
      result
        .map((item, index) => ({
          x: item.canvas_x,
          y: item.canvas_y,
          meldingId: item.melding_id,
          label: String(index + 1),
        }))
        .filter((marker) => marker.x != null && marker.y != null)
    )
  }

  useEffect(() => {
    fetchBestanden()
  }, [material?.id])

  useEffect(() => () => clearTimeout(highlightTimer.current), [])

  useEffect(() => {
    const canvas = canvasRef.current
    const wrapper = wrapperRef.current
    if (!canvas || !wrapper) return

    const ctx = canvas.getContext("2d")

    const draw = () => {
      // clientWidth/Height, not getBoundingClientRect: antd's Modal mounts with a
      // zoom transform, and getBoundingClientRect returns the *transformed* box —
      // near zero mid-animation, which would size the canvas to nothing.
      const width = wrapper.clientWidth
      const height = wrapper.clientHeight
      if (!width || !height) return

      const dpr = 1 // fix for now to avoid blurriness

      canvas.width = width * dpr
      canvas.height = height * dpr

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      ctx.clearRect(0, 0, width, height)

      const rect = getImageRect(backgroundRef.current, width, height)
      imageRectRef.current = rect
      drawSchematic(ctx, backgroundRef.current, rect)

      const placed = spreadMarkers(
        markers.map((marker) => ({ ...marker, ...toCanvasPoint(rect, marker) })),
        MARKER_RADIUS
      )
      placedRef.current = placed

      // leader lines first, so they stay below the markers
      placed.forEach(({ x, y, realX, realY }) => {
        if (x === realX && y === realY) return
        ctx.beginPath()
        ctx.moveTo(realX, realY)
        ctx.lineTo(x, y)
        ctx.strokeStyle = colors.black
        ctx.lineWidth = 1.5
        ctx.stroke()
        ctx.beginPath()
        ctx.arc(realX, realY, 3, 0, 2 * Math.PI)
        ctx.fillStyle = colors.black
        ctx.fill()
      })

      placed.forEach(({ x, y, label }) => {
        ctx.beginPath()
        ctx.arc(x, y, MARKER_RADIUS, 0, 2 * Math.PI)
        ctx.fillStyle = colors.orange
        ctx.fill()
        ctx.fillStyle = colors.black
        ctx.font = "bold 14px sans-serif"
        ctx.textAlign = "center"
        ctx.textBaseline = "middle"
        ctx.fillText(label, x, y)
      })
    }

    draw()

    const observer = new ResizeObserver(draw)
    observer.observe(wrapper)

    return () => {
      observer.disconnect()
    }
  }, [markers, backgroundLoaded])

  // new, unsaved markers have no melding and can't be clicked
  const markerAt = (point) =>
    placedRef.current.find(
      (marker) => marker.meldingId && Math.hypot(marker.x - point.x, marker.y - point.y) <= MARKER_RADIUS
    )

  const scrollToPhoto = (meldingId) => {
    photoRefs.current[meldingId]?.scrollIntoView({ behavior: "smooth", block: "center" })
    setHighlightedMelding(meldingId)
    clearTimeout(highlightTimer.current)
    highlightTimer.current = setTimeout(() => setHighlightedMelding(null), 1500)
  }

  const handleCanvasMouseMove = (event) => {
    if (loading || !imageRectRef.current) return
    const point = canvasPoint(event, canvasRef.current)
    canvasRef.current.style.cursor = markerAt(point)
      ? "pointer"
      : toImagePosition(imageRectRef.current, point) ? "crosshair" : "default"
  }

  const handleCanvasClick = (event) => {
    const canvas = canvasRef.current
    if (!canvas || loading) return

    const point = canvasPoint(event, canvas)
    const marker = markerAt(point)
    if (marker) {
      scrollToPhoto(marker.meldingId)
      return
    }

    const position = imageRectRef.current && toImagePosition(imageRectRef.current, point)
    if (!position) return

    setPendingCoordinates(position)
    setMeldingModalOpen(true)
    addMarker({ ...position, label: "+" })
  }

  // AddMeldingModal calls onClose after a successful save too, so the refetch
  // in closeMeldingModal both drops a cancelled marker and shows a saved one
  const handleSaveMelding = (data) =>
    // wait for the photo uploads, otherwise the refetch misses the new photo
    dispatch(addMelding(data, { awaitUploads: true }))

  const closeMeldingModal = () => {
    setMeldingModalOpen(false)
    setPendingCoordinates(null)
    fetchBestanden()
  }

  return (
    <Modal
      title={$t("Schadekaart")}
      open={true}
      onCancel={closeModal}
      footer={[
        <Button key="close" type="primary" onClick={closeModal}>
          {$t("Gereed")}
        </Button>,
      ]}
      width={isMobile ? "100%" : 720}
      styles={{ body: { height: '80vh' } }}
    >
      <div
        ref={wrapperRef}
        style={{
          width: "100%",
          height: isMobile ? "100%" : 600,
          marginBottom: 20,
          position: "relative",
        }}
      >
        <canvas
          ref={canvasRef}
          style={{ display: "block", width: "100%", height: "100%", cursor: loading ? "progress" : "crosshair" }}
          onClick={handleCanvasClick}
          onMouseMove={handleCanvasMouseMove}
        />
        {loading && (
          <Flex
            align="center"
            justify="center"
            style={{ position: "absolute", inset: 0 }}
          >
            <Spin />
          </Flex>
        )}
      </div>
      <Flex wrap gap="small">
        {bestanden.map((bestand, index) => {
          const markerLabel = index + 1;
          const openMeldingProps = {
            onClick: () => history.push(`/report/${bestand.melding_id}`),
            onMouseMove: (e) => setHoverPosition({ x: e.clientX, y: e.clientY }),
            onMouseLeave: () => setHoverPosition(null),
          };

          return (
            <div
              // bestanden.json returns one item per melding
              key={bestand.melding_id}
              ref={(el) => { photoRefs.current[bestand.melding_id] = el }}
              style={{
                position: "relative",
                width: "100%",
                height: bestand.file ? 300 : 120,
                borderRadius: 8,
                overflow: "hidden",
                outline: highlightedMelding === bestand.melding_id ? `4px solid ${colors.orange}` : "none",
                outlineOffset: -4,
                transition: "outline-color 0.3s",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: 8,
                  left: 8,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  right: 8,
                  zIndex: 1,
                  pointerEvents: "none",
                }}
              >
                <span
                  style={{
                    display: "flex",
                    flex: "0 0 30px",
                    width: 30,
                    height: 30,
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "50%",
                    background: colors.orange,
                    border: `2px solid ${colors.gray_dark}`,
                    color: colors.gray_dark,
                    fontWeight: 700,
                  }}
                >
                  {markerLabel}
                </span>
                {bestand.titel && (
                  <span
                    style={{
                      minWidth: 0,
                      overflow: "hidden",
                      padding: "5px 8px",
                      borderRadius: 4,
                      background: "rgba(0, 0, 0, 0.72)",
                      color: "#fff",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    {bestand.titel}
                    <MeldingStatusTag status={bestand?.status} category={bestand?.category} />
                  </span>
                )}
              </div>
              {bestand.file ? (
                <img
                  src={`${config.media_prefix}${bestand.file}`}
                  alt={bestand.naam || "bestand"}
                  style={{ width: "100%", height: "100%", objectFit: "cover", cursor: "pointer" }}
                  {...openMeldingProps}
                />
              ) : (
                <Flex
                  align="center"
                  justify="center"
                  style={{ width: "100%", height: "100%", background: colors.gray_lighter, color: colors.gray, cursor: "pointer" }}
                  {...openMeldingProps}
                >
                  {$t("Geen afbeelding")}
                </Flex>
              )}
            </div>
          )
        })}
      </Flex>
      {hoverPosition && (
        <div
          style={{
            position: "fixed",
            left: hoverPosition.x + 12,
            top: hoverPosition.y + 16,
            pointerEvents: "none",
            zIndex: 2000,
            padding: "6px 8px",
            borderRadius: 6,
            background: "rgba(0, 0, 0, 0.85)",
            color: "#fff",
            fontSize: 14,
            whiteSpace: "nowrap",
          }}
        >
          {$t("Bekijk melding")}
        </div>
      )}
      {meldingModalOpen && (
        <AddMeldingModal
          visible={meldingModalOpen}
          onClose={closeMeldingModal}
          onSave={handleSaveMelding}
          activeMelding={null}
          materiaal={material}
          currentMaterial={material}
          currentWorkorderID={null}
          initialPosition={pendingCoordinates}
        />
      )}
    </Modal>
  )
}
